import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Webhook, Loader2, Copy, CheckCircle2, XCircle, ExternalLink, Clock,
} from "lucide-react";
import { api } from "../lib/api";

function fmtRelative(iso) {
  if (!iso) return "Nunca recebido";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `há ${Math.floor(diff)} segundos`;
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function WhatsAppWebhookConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/webhook/whatsapp/config");
      setCfg(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar configuração do webhook.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    if (!cfg?.webhook?.url) return;
    try {
      await navigator.clipboard.writeText(cfg.webhook.url);
      setCopied(true);
      toast.success("URL copiada!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar, selecione manualmente.");
    }
  };

  const recent = cfg?.webhook?.last_received_at
    ? (Date.now() - new Date(cfg.webhook.last_received_at).getTime()) / 1000 < 600
    : false;

  return (
    <section data-testid="webhook-config" className="bg-white border border-border rounded-2xl p-5 sm:p-6">
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-700 flex items-center gap-1.5">
            <Webhook className="w-3 h-3" /> Receptor de mensagens
          </p>
          <h3 className="font-display text-xl text-navy mt-1">URL do Webhook do WhatsApp</h3>
          <p className="text-[12px] text-navy/55 mt-1.5 max-w-md">
            Configure esta URL no Twilio Console em <strong>Messaging → Try it out → Send a WhatsApp message → Sandbox settings → When a message comes in</strong>. Toda mensagem recebida será encaminhada à IA pastoral.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="text-center text-navy/55 py-6">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…
        </div>
      ) : !cfg ? null : (
        <div className="space-y-4">
          <div className="bg-[hsl(var(--parchment))] border border-border rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-navy/55 mb-1.5">URL canônica</p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[12px] sm:text-[13px] font-mono text-navy bg-white border border-border rounded-md px-3 py-2 flex-1 min-w-0 break-all" data-testid="webhook-url">
                {cfg.webhook.url}
              </code>
              <button
                onClick={copy}
                data-testid="webhook-copy-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-navy text-white hover:bg-emerald-700 text-[11px] uppercase tracking-widest transition-colors"
              >
                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
            <p className="text-[10px] text-navy/45 mt-2">
              Método: <strong>POST</strong> · Tipo: <strong>application/x-www-form-urlencoded</strong> (padrão Twilio)
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Status
              ok={cfg.ready_for_inbound_webhook}
              label="Pronto para receber"
              detail={cfg.ready_for_inbound_webhook
                ? "Twilio + IA configurados"
                : "Faltam credenciais Twilio ou IA"}
              testid="webhook-ready"
            />
            <Status
              ok={recent}
              label="Última mensagem recebida"
              detail={fmtRelative(cfg.webhook.last_received_at)}
              icon={Clock}
              testid="webhook-last-received"
            />
          </div>

          <details className="text-[12px] text-navy/55">
            <summary className="cursor-pointer hover:text-navy">
              Como configurar passo a passo
            </summary>
            <ol className="mt-2 space-y-1.5 pl-4 list-decimal">
              <li>Acesse <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-0.5">console.twilio.com <ExternalLink className="w-2.5 h-2.5" /></a>.</li>
              <li>Vá em <strong>Messaging → Try it out → Send a WhatsApp message</strong>.</li>
              <li>Aba <strong>Sandbox settings</strong>.</li>
              <li>Cole a URL acima no campo <strong>"When a message comes in"</strong>.</li>
              <li>Salve. Pronto: toda mensagem recebida cai aqui no painel.</li>
            </ol>
            <p className="mt-2 text-[11px] text-navy/45">
              URL legada (mantida por compatibilidade): <code className="font-mono">{cfg.webhook.legacy_url}</code>
            </p>
          </details>
        </div>
      )}
    </section>
  );
}

function Status({ ok, label, detail, icon: Icon, testid }) {
  const Comp = Icon || (ok ? CheckCircle2 : XCircle);
  return (
    <div className="bg-[hsl(var(--parchment))] border border-border rounded-xl px-3 py-2.5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-navy/50">{label}</p>
      <p className={`text-sm font-medium mt-0.5 truncate flex items-center gap-1.5 ${ok ? "text-navy" : "text-navy/60"}`}>
        <Comp className={`w-3.5 h-3.5 flex-shrink-0 ${ok ? "text-emerald-600" : "text-navy/40"}`} />
        <span className="truncate">{detail}</span>
      </p>
    </div>
  );
}
